import { tMessage } from '@/core/form'

export class PostalCode {
  /**
   * Regular expression pattern for validating postal codes
   * Accepts both formats: 1234567 and 123-4567
   */
  static readonly PATTERN = /^(\d{3}-?\d{4}|\d{7})$/

  /**
   * Translation key for invalid postal code format error message
   */
  static readonly FORMAT_ERROR_KEY = 'postalCodeFormatError'

  /**
   * @param postalCodeLike value like postal code
   *
   * @returns formatted postal code or pure value.
   */
  static format(postalCodeLike: string) {
    const match = postalCodeLike.match(/^(\d{3})(\d{4})$/)
    if (match) {
      return `〒${match[1]}-${match[2]}`
    }

    return `〒${postalCodeLike}`
  }

  /**
   * Normalizes a postal code by removing hyphens
   *
   * @param postalCode postal code that may contain hyphens
   * @returns normalized postal code without hyphens
   */
  static normalize(postalCode: string): string {
    // Remove all hyphens from the postal code
    return postalCode.replace(/-/g, '')
  }

  /**
   * Validates if the postal code format is correct
   *
   * @param postalCode postal code to validate
   * @returns true if the postal code format is valid, false otherwise
   */
  static isValid(postalCode: string): boolean {
    return PostalCode.PATTERN.test(postalCode)
  }

  /**
   * Creates a custom validation function for use with form validation libraries
   *
   * @param required whether the postal code is required
   * @returns a validation function that can be used with form libraries
   */
  static createValidator(required = true) {
    return (value: string, helpers: any) => {
      if (!required && (!value || value === '')) {
        return value
      }

      if (!PostalCode.isValid(value)) {
        return helpers.message({
          custom: tMessage(PostalCode.FORMAT_ERROR_KEY),
        })
      }

      return value
    }
  }
}
